import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

const TRACK_WIDTH = 14;
const THUMB_MIN_HEIGHT = 20;

const SliceScrollbar = memo(({ currentSlice = 0, totalSlices = 0, onSliceChange }) => {
  const trackRef = useRef(null);
  const dragStateRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);

  const lastSliceIndex = Math.max(0, totalSlices - 1);
  const safeIndex = Math.min(Math.max(0, currentSlice), lastSliceIndex);

  const computeIndexFromClientY = useCallback((clientY) => {
    const track = trackRef.current;
    if (!track || lastSliceIndex <= 0) return 0;
    const rect = track.getBoundingClientRect();
    const trackHeight = rect.height;
    const thumbHeight = Math.max(THUMB_MIN_HEIGHT, trackHeight / totalSlices);
    const usable = Math.max(1, trackHeight - thumbHeight);
    const localY = clientY - rect.top - thumbHeight / 2;
    const ratio = Math.min(1, Math.max(0, localY / usable));
    return Math.round(ratio * lastSliceIndex);
  }, [lastSliceIndex, totalSlices]);

  const handleTrackMouseDown = useCallback((e) => {
    if (lastSliceIndex <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    const target = computeIndexFromClientY(e.clientY);
    if (target !== safeIndex) onSliceChange?.(target);
    dragStateRef.current = { mode: 'track' };
    setDragging(true);
  }, [computeIndexFromClientY, lastSliceIndex, onSliceChange, safeIndex]);

  const handleThumbMouseDown = useCallback((e) => {
    if (lastSliceIndex <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    const track = trackRef.current;
    const rect = track.getBoundingClientRect();
    const trackHeight = rect.height;
    const thumbHeight = Math.max(THUMB_MIN_HEIGHT, trackHeight / totalSlices);
    dragStateRef.current = {
      mode: 'thumb',
      startY: e.clientY,
      startIndex: safeIndex,
      usable: Math.max(1, trackHeight - thumbHeight),
    };
    setDragging(true);
  }, [lastSliceIndex, safeIndex, totalSlices]);

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e) => {
      const state = dragStateRef.current;
      if (!state) return;
      let next;
      if (state.mode === 'thumb') {
        const deltaY = e.clientY - state.startY;
        const deltaIndex = (deltaY / state.usable) * lastSliceIndex;
        next = Math.round(state.startIndex + deltaIndex);
      } else {
        next = computeIndexFromClientY(e.clientY);
      }
      next = Math.min(lastSliceIndex, Math.max(0, next));
      if (next !== safeIndex) onSliceChange?.(next);
    };

    const handleUp = () => {
      dragStateRef.current = null;
      setDragging(false);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, computeIndexFromClientY, lastSliceIndex, onSliceChange, safeIndex]);

  if (totalSlices <= 1) return null;

  const thumbHeightStyle = `max(${THUMB_MIN_HEIGHT}px, ${(100 / totalSlices).toFixed(4)}%)`;
  const thumbTopRatio = lastSliceIndex > 0 ? safeIndex / lastSliceIndex : 0;
  const thumbTopStyle = `calc((100% - ${thumbHeightStyle}) * ${thumbTopRatio})`;

  const active = dragging || hovered;

  return (
    <div
      ref={trackRef}
      onMouseDown={handleTrackMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: TRACK_WIDTH,
        backgroundColor: active ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0.35)',
        zIndex: 11,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        userSelect: 'none',
      }}
    >
      <div
        onMouseDown={handleThumbMouseDown}
        style={{
          position: 'absolute',
          left: 2,
          right: 2,
          top: thumbTopStyle,
          height: thumbHeightStyle,
          backgroundColor: active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)',
          borderRadius: 3,
          cursor: 'grab',
          transition: 'background-color 120ms ease',
        }}
      />
    </div>
  );
});

export default SliceScrollbar;
